<template>
  <main class="login-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="seo" />

    <section class="login-section" aria-labelledby="login-heading">
      <v-container class="login-container">
        <v-row justify="center">
          <v-col cols="12" sm="8" md="5" lg="4">
            <v-card class="login-card" variant="flat">
              <div class="login-card__brand">
                <!--
                  Reuse the existing ThinkSpatial logo asset (Requirement 8.3);
                  no new logo artwork is introduced.
                -->
                <img
                  :src="logoSrc"
                  alt="ThinkSpatial"
                  class="login-card__logo"
                />
              </div>

              <h1 id="login-heading" class="login-heading">
                {{ content.heading }}
              </h1>
              <p class="login-subheading">{{ content.subheading }}</p>

              <!--
                The submit is intercepted with `.prevent`; `onSubmit` decides
                whether to invoke the (currently stubbed) auth handler. While
                the Login Feature_Flag is disabled, NO credentials are sent to
                any authentication endpoint (Requirement 3.5).
              -->
              <v-form novalidate class="login-form" @submit.prevent="onSubmit">
                <!-- Notice shown while login is disabled so the screen renders
                     meaningfully without contacting any endpoint. -->
                <v-alert
                  v-if="status === 'disabled'"
                  type="info"
                  variant="tonal"
                  class="mb-4"
                  role="status"
                >
                  {{ content.disabledText }}
                </v-alert>

                <!-- Error state surfaced once the handler is wired up. -->
                <v-alert
                  v-if="status === 'error'"
                  :type="errorKind === 'expired' ? 'warning' : 'error'"
                  variant="tonal"
                  class="mb-4"
                  role="alert"
                >
                  {{ errorMessage }}
                </v-alert>

                <!--
                  Email + password fields. The `id`/`label` pairing makes
                  Vuetify render a <label for> bound to each input, so every
                  field has a programmatically associated label (accessibility,
                  Requirement 9.6 pattern).
                -->
                <v-text-field
                  id="login-email"
                  v-model="email"
                  :label="content.fields.email.label"
                  :placeholder="content.fields.email.placeholder"
                  :error-messages="emailError ? [emailError] : []"
                  name="email"
                  type="email"
                  autocomplete="email"
                  variant="outlined"
                />

                <v-text-field
                  id="login-password"
                  v-model="password"
                  :label="content.fields.password.label"
                  :placeholder="content.fields.password.placeholder"
                  :error-messages="passwordError ? [passwordError] : []"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  variant="outlined"
                />

                <CtaButton
                  type="submit"
                  :label="content.submitLabel"
                  :loading="status === 'submitting'"
                  :disabled="status === 'submitting'"
                  block
                  class="login-submit"
                />
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </main>
</template>

<script>
// LoginLandingPage — the hidden Login_Landing_Page (Requirement 3).
//
// Renders a brand-styled login screen with email and password fields and a
// submit control (Requirement 3.1), applying the Brand_System styling used
// across the rest of the site (Requirement 3.4).
//
// IMPORTANT — auth wiring boundary (Requirements 3.5, 3.6):
//   While the Login Feature_Flag is disabled, this page MUST NOT submit
//   credentials to any authentication endpoint. All network concerns are
//   isolated to the single `authenticate()` method below, which is the ONLY
//   place that needs to change to wire `POST /api/auth/login`. The rest of the
//   page (layout, fields, validation hooks, state handling) is final and needs
//   no redesign when login is enabled later.
//
// The email draft is persisted via the dedicated Pinia store so a returning
// visitor sees their last-typed email; the password is NEVER persisted.
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset.
import { mapStores } from 'pinia'
import SeoHead from '@/components/SeoHead.vue'
import CtaButton from '@/components/CtaButton.vue'
import { runtimeConfig } from '@/config/runtimeConfig'
import { useLoginDraftStore } from '@/stores/loginDraft'
import { useAuthStore } from '@/stores/auth'
import AuthenticationApi from '@/services/AuthenticationApi'
import { mapLoginError, LOGIN_ERROR_MESSAGES } from '@/pages/mapLoginError'
import { resolvePostLoginTarget, ADMIN_HOME } from '@/router/postLogin'
import logoSrc from '@/assets/svg/ts-logo.svg'

// Maximum time (ms) to wait for the login endpoint before treating the attempt
// as a connection failure (Requirement 3.3).
const LOGIN_TIMEOUT_MS = 30000

// Email format check: a local part, an `@` separator, and a domain part
// (Requirement 1.1). Signature verification is not attempted here — this only
// blocks obviously malformed input before any request is sent.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+$/

// Fixed, user-facing copy for each error kind. The `invalid`/`service`/
// `connection`/`generic` messages are the same fixed set produced by
// `mapLoginError` (never a server payload, stack trace, or credential —
// Requirement 11.3). `expired` is the distinct session-ended message shown when
// the page is reached after a JWT rejection/expiry (Requirement 7.5), and
// `navigation` covers a post-login redirect that could not be opened
// (Requirement 2.5).
const ERROR_MESSAGES = Object.freeze({
  ...LOGIN_ERROR_MESSAGES,
  expired: 'Your session has ended. Please sign in again.',
  navigation: 'We signed you in, but could not open the requested page. Taking you to your dashboard.'
})

// Co-located SEO + copy for the Login screen. Kept inline (rather than in the
// shared content modules for Home/About/Contact) because the Login screen is a
// hidden, single-purpose utility page.
const content = {
  heading: 'Sign in',
  subheading: 'Access your ThinkSpatial account.',
  disabledText: 'Login is not yet available. This screen is a preview.',
  errorText: 'We could not sign you in. Please check your details and try again.',
  submitLabel: 'Sign in',
  fields: {
    email: {
      label: 'Email',
      placeholder: 'you@example.com',
    },
    password: {
      label: 'Password',
      placeholder: 'Your password',
    },
  },
}

const seo = {
  title: 'Sign in | ThinkSpatial',
  description: 'Sign in to your ThinkSpatial account.',
}

export default {
  name: 'LoginLandingPage',
  components: {
    SeoHead,
    CtaButton,
  },
  data () {
    return {
      content,
      seo,
      logoSrc,
      // Field models. `email` is synced to the persisted draft store; the
      // password is held in component state only and never persisted.
      email: '',
      password: '',
      // UI state machine: idle | disabled | submitting | error.
      status: 'idle',
      // Which fixed message the error alert shows when `status === 'error'`.
      // One of: 'invalid' | 'service' | 'connection' | 'generic' | 'expired'
      // | 'navigation' | null.
      errorKind: null,
      // Per-field validation messages (Requirement 1.2).
      emailError: null,
      passwordError: null
    }
  },
  computed: {
    // Pinia Options-API binding → exposes `this.loginDraftStore` and
    // `this.authStore`.
    ...mapStores(useLoginDraftStore, useAuthStore),
    // Whether the Login Feature_Flag is enabled. Drives whether the submit
    // handler is permitted to contact the auth endpoint (Requirement 3.5).
    loginEnabled () {
      return runtimeConfig.features.login === true
    },
    // The fixed message to show for the current error kind. Falls back to the
    // generic copy so the alert is never empty.
    errorMessage () {
      return ERROR_MESSAGES[this.errorKind] || ERROR_MESSAGES.generic
    }
  },
  watch: {
    // Persist the email draft as the visitor types (password excluded).
    email (value) {
      this.loginDraftStore.setEmail(value)
    },
  },
  created () {
    // Restore the previously drafted email, if any. The password field is left
    // empty and is never persisted (Requirement 12.4).
    this.email = this.loginDraftStore.email
    // Reflect the disabled state in the UI without performing any network call
    // (Requirements 9.3, 9.4).
    if (!this.loginEnabled) {
      this.status = 'disabled'
      return
    }
    // When the page is reached after a session expiry/rejection, show the
    // distinct session-ended message (Requirement 7.5).
    if (this.$route.query.reason === 'expired') {
      this.errorKind = 'expired'
      this.status = 'error'
    }
  },
  methods: {
    // Form submit entry point. Gates on the Login Feature_Flag so that, while
    // disabled, NO credentials are sent anywhere (Requirements 1.3, 9.3, 9.4).
    // Pre-submit validation blocks obviously invalid input and sends nothing
    // (Requirement 1.2). A duplicate-submit guard prevents concurrent requests
    // while one is in flight (Requirement 1.4).
    async onSubmit () {
      if (!this.loginEnabled) {
        this.status = 'disabled'
        return
      }

      // Duplicate-submit guard: ignore re-entry while a request is in flight.
      if (this.status === 'submitting') {
        return
      }

      // Pre-submit validation. On failure, block the submit, send nothing, and
      // surface which field is invalid (Requirement 1.2).
      if (!this.validate()) {
        return
      }

      const credentials = { email: this.email.trim(), password: this.password }

      this.status = 'submitting'
      this.errorKind = null

      try {
        const { data } = await this.authenticate(credentials)
        // Persist the session (JWT + non-sensitive profile) via the store
        // (Requirement 1.5), then clear the password from component state
        // (Requirement 1.6). The email draft is intentionally preserved.
        this.authStore.setSession({ token: data.token, profile: data.user })
        this.password = ''
        this.navigateAfterLogin()
      } catch (err) {
        // On any failure: clear the password (Requirement 3.5), map the error
        // to a fixed message (Requirements 3.1–3.4), return to an interactive
        // state (Requirement 3.6), and store nothing (Requirement 3.7).
        this.password = ''
        const { kind } = mapLoginError(err)
        this.errorKind = kind
        this.status = 'error'
      }
    },

    // Validate the form before any request is sent. Sets per-field error
    // messages and returns whether the form may be submitted (Requirement 1.2).
    validate () {
      this.emailError = null
      this.passwordError = null
      let ok = true

      const email = (this.email || '').trim()
      if (!email) {
        this.emailError = 'Enter your email address.'
        ok = false
      } else if (!EMAIL_PATTERN.test(email)) {
        this.emailError = 'Enter a valid email address.'
        ok = false
      }

      if (!this.password) {
        this.passwordError = 'Enter your password.'
        ok = false
      }

      return ok
    },

    // Resolve and perform the post-login navigation. The captured redirect is
    // validated (never trusted) and only honoured for internal admin paths;
    // anything else falls back to the default Admin_Shell route (Requirements
    // 2.1–2.4). If the navigation itself fails, fall back to the default shell
    // and surface a message (Requirement 2.5).
    navigateAfterLogin () {
      const target = resolvePostLoginTarget(this.$route.query.redirect, this.$router)
      this.$router.push(target).catch(() => {
        this.errorKind = 'navigation'
        this.status = 'error'
        this.$router.push(ADMIN_HOME).catch(() => {})
      })
    },

    // ───────────────────────────────────────────────────────────────────────
    // AUTH WIRING POINT — connects to `POST /api/auth/login` via the existing
    // AuthenticationApi service. The password rides only in the request body
    // (Requirement 11.1); a 30s timeout bounds the wait so an unresponsive
    // endpoint surfaces as a connection error (Requirement 3.3).
    // ───────────────────────────────────────────────────────────────────────
    authenticate (credentials) {
      return AuthenticationApi.login(credentials, { timeout: LOGIN_TIMEOUT_MS })
    },
  },
}
</script>

<style lang="scss" scoped>
.login-section {
  // Light-themed, brand-neutral background (Requirement 8.6).
  padding: 96px 0;
  background-color: #f7f5f4;
  min-height: 70vh;
  display: flex;
  align-items: center;
}

.login-container {
  width: 100%;
}

.login-card {
  background-color: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 10px 30px rgba(65, 40, 27, 0.08);
}

.login-card__brand {
  text-align: center;
  margin-bottom: 24px;
}

.login-card__logo {
  height: 48px;
  width: auto;
}

.login-heading {
  font-size: 1.9rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 8px;
  text-align: center;
}

.login-subheading {
  font-size: 1rem;
  color: #4a4a4a;
  margin-bottom: 24px;
  text-align: center;
}

.login-form {
  width: 100%;
}

.login-submit {
  margin-top: 8px;
}
</style>
